import { useEffect, useState } from 'react';
import { Check, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

const REWARD_OPTIONS = [
  { value: 'free_month',          label: '1 mês grátis no plano mensal' },
  { value: 'free_month_annual',   label: '1 mês grátis no plano anual' },
  { value: 'synastry_credit',     label: 'Crédito de Sinastria' },
  { value: 'astromatch_credits',  label: 'Créditos de AstroMatch' },
  { value: 'question_credits',    label: 'Créditos de Perguntar aos Astros' },
  { value: 'astrocartografia',    label: 'Acesso à Astrocartografia' },
];

const PAGE_OPTIONS = [
  { value: 'forecast',     label: 'Previsão' },
  { value: 'birth-chart',  label: 'Mapa Natal' },
  { value: 'synastry',     label: 'Sinastria' },
  { value: 'astromatch',   label: 'AstroMatch' },
  { value: 'questions',    label: 'Perguntar aos Astros' },
  { value: 'tarot',        label: 'Tarot' },
  { value: 'numerology',   label: 'Numerologia' },
  { value: 'dashboard',    label: 'Dashboard' },
  { value: 'profile',      label: 'Perfil' },
];

function useSetting(key: string, def: string) {
  const [val, setVal] = useState(def);
  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', key).maybeSingle()
      .then(({ data }) => { if (data?.value !== undefined) setVal(String(data.value)); });
  }, [key]);
  async function save(v: string) {
    setVal(v);
    await supabase.from('platform_settings').upsert({ key, value: v, description: key }, { onConflict: 'key' });
  }
  return [val, save] as const;
}

export function AdminReferralPromoPage() {
  const [active,       setActive]      = useSetting('referral_promo_active',        'false');
  const [rewardType,   setRewardType]  = useSetting('referral_promo_reward_type',   'free_month');
  const [rewardAmount, setRewardAmount]= useSetting('referral_promo_reward_amount', '1');
  const [promoLabel,   setPromoLabel]  = useSetting('referral_promo_label',         'Ganhe 1 mês grátis');
  const [expiresAt,    setExpiresAt]   = useSetting('referral_promo_expires_at',    '');
  const [pages,        setPages]       = useSetting('referral_promo_pages',         '["dashboard"]');
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const selectedPages: string[] = (() => { try { return JSON.parse(pages); } catch { return []; } })();

  function togglePage(page: string) {
    const next = selectedPages.includes(page)
      ? selectedPages.filter(p => p !== page)
      : [...selectedPages, page];
    setPages(JSON.stringify(next));
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    await Promise.all([
      supabase.from('platform_settings').upsert({ key: 'referral_promo_active',        value: active,       description: 'referral_promo_active' },        { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({ key: 'referral_promo_reward_type',   value: rewardType,   description: 'referral_promo_reward_type' },   { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({ key: 'referral_promo_reward_amount', value: rewardAmount, description: 'referral_promo_reward_amount' }, { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({ key: 'referral_promo_label',         value: promoLabel,   description: 'referral_promo_label' },         { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({ key: 'referral_promo_expires_at',    value: expiresAt,    description: 'referral_promo_expires_at' },    { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({ key: 'referral_promo_pages',         value: pages,        description: 'referral_promo_pages' },         { onConflict: 'key' }),
    ]);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '1.5rem 1rem' }}>
      <AdminPageHeader
        title="Promo Indicação"
        objective="Configure campanhas onde usuários que recomendam o Professya ganham uma recompensa. Defina o prêmio, o prazo e onde o banner de indicação aparece no site. Ative/desative sem precisar de suporte técnico."
      />

      {/* Active toggle */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.2rem', fontSize: 'var(--text-label)' }}>
              Promoção ativa
            </p>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
              {active === 'true' ? (isExpired ? '⚠️ Expirada, desativar ou estender a data' : '🟢 Em ar') : '⚪ Desativada'}
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={active === 'true'}
              onChange={e => setActive(e.target.checked ? 'true' : 'false')}
              style={{ width: '18px', height: '18px', accentColor: 'var(--petrol)' }}
            />
            <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>Ativada</span>
          </label>
        </div>
      </div>

      {/* Reward configuration */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 1rem', fontSize: 'var(--text-label)' }}>
          Configuração da Recompensa
        </p>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label" title="Recompensa: benefício que o usuário recebe ao indicar alguém que se cadastra e paga. Escolha um prêmio relevante para aumentar a taxa de participação.">
            Tipo de recompensa (para quem indica)
          </label>
          <select className="form-input" value={rewardType} onChange={e => setRewardType(e.target.value)}>
            {REWARD_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">Quantidade</label>
          <input
            type="number"
            min="1"
            max="99"
            className="form-input"
            value={rewardAmount}
            onChange={e => setRewardAmount(e.target.value)}
            style={{ maxWidth: '120px' }}
          />
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            Ex.: 1 = 1 mês grátis / 3 = 3 créditos
          </p>
        </div>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">Texto exibido na interface (curto, ex.: "Ganhe 1 mês grátis")</label>
          <input
            type="text"
            className="form-input"
            value={promoLabel}
            onChange={e => setPromoLabel(e.target.value)}
            placeholder="Ganhe 1 mês grátis"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Data de expiração (deixe em branco para sem prazo)</label>
          <input
            type="date"
            className="form-input"
            value={expiresAt ? expiresAt.slice(0, 10) : ''}
            onChange={e => setExpiresAt(e.target.value)}
            style={{ maxWidth: '220px' }}
          />
          {isExpired && expiresAt && (
            <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', marginTop: '0.25rem' }}>
              ⚠️ Esta data já passou. A promoção não aparecerá para usuários.
            </p>
          )}
        </div>

        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: '0.5rem 0 0', lineHeight: 1.55, borderTop: '1px solid var(--border-subtle)', paddingTop: '0.75rem' }}>
          💡 Dica: prêmios em créditos (Perguntas aos Astros, Sinastria) tendem a aumentar o engajamento do usuário indicador com a plataforma.
        </p>
      </div>

      {/* Campaign referrals counter */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.3rem', fontSize: 'var(--text-label)' }}>
          Total de indicações desta campanha
        </p>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--petrol)', margin: 0 }}>
          -
        </p>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
          Dados disponíveis após integração com o banco de dados.
        </p>
      </div>

      {/* Pages where banner appears */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.5rem', fontSize: 'var(--text-label)' }}>
          Páginas onde o banner aparece
        </p>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.875rem' }}>
          Selecione em quais páginas o convite de indicação será exibido para usuários logados.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {PAGE_OPTIONS.map(p => {
            const selected = selectedPages.includes(p.value);
            return (
              <button
                key={p.value}
                onClick={() => togglePage(p.value)}
                className={selected ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ borderRadius: '999px' }}
              >
                {selected && <Check size={12} style={{ marginRight: '0.25rem' }} />}
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview */}
      <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
          <Info size={14} style={{ color: 'var(--text-muted)' }} />
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0, fontWeight: 600 }}>
            Preview do banner (como aparece para a usuária)
          </p>
        </div>
        <div style={{
          padding: '0.875rem 1rem',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(31,78,95,0.3)',
          background: 'rgba(31,78,95,0.06)',
          fontSize: 'var(--text-caption)',
          color: 'var(--text-secondary)',
          lineHeight: 1.55,
        }}>
          🎁 <strong style={{ color: 'var(--text-primary)' }}>Indique uma amiga e {promoLabel || 'ganhe uma recompensa'}.</strong>{' '}
          Quando ela assinar qualquer plano, você recebe automaticamente. Sem complicação.
          {' '}
          <span style={{ color: 'var(--petrol)', fontWeight: 600, cursor: 'pointer' }}>Ver meu link →</span>
        </div>
      </div>

      {/* Save */}
      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ minWidth: '160px' }}
      >
        {saving ? 'Salvando...' : saved ? '✓ Salvo' : 'Salvar configuração'}
      </button>
    </div>
  );
}
